import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Image as ImageIcon } from '@mui/icons-material';
import { Chapitre } from '../../types';
import { formatDate } from '../../utils/dates';

interface ChapitreDetailProps {
  chapitre: Chapitre;
}

const ChapitreDetail: React.FC<ChapitreDetailProps> = ({ chapitre }) => {
  return (
    <Box>
      {/* En-tête */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {chapitre.titre}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          {chapitre.niveauDifficulte && (
            <Chip
              label={chapitre.niveauDifficulte}
              color={
                chapitre.niveauDifficulte === 'facile'
                  ? 'success'
                  : chapitre.niveauDifficulte === 'moyen'
                  ? 'warning'
                  : 'error'
              }
              size="small"
            />
          )}
          <Chip
            label={(chapitre.statut || 'published') === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
            color={(chapitre.statut || 'published') === 'draft' ? 'warning' : 'success'}
            size="small"
          />
          <Chip
            label={chapitre.estActif ? 'Actif' : 'Inactif'}
            color={chapitre.estActif ? 'success' : 'default'}
            size="small"
            icon={chapitre.estActif ? <CheckCircle /> : <RadioButtonUnchecked />}
          />
          <Chip
            label={`Ordre: ${chapitre.ordre}`}
            color="default"
            size="small"
            variant="outlined"
          />
          {chapitre.tempsEstimeTotalMinutes && (
            <Chip
              label={`${chapitre.tempsEstimeTotalMinutes} min`}
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          {chapitre.imageUrl && (
            <Chip
              icon={<ImageIcon />}
              label="Image disponible"
              color="secondary"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Image du chapitre */}
      {chapitre.imageUrl && (
        <Paper sx={{ p: 2, mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Image du chapitre
          </Typography>
          <Box
            component="img"
            src={chapitre.imageUrl}
            alt={chapitre.titre}
            sx={{
              maxWidth: '100%',
              maxHeight: 300,
              borderRadius: 1,
              mt: 1,
            }}
            onError={(event) => {
              (event.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </Paper>
      )}

      {/* Informations générales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Informations générales
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {chapitre.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Matière
                </Typography>
                <Typography variant="body2">{chapitre.matiereId}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Ordre
                </Typography>
                <Typography variant="body2">{chapitre.ordre}</Typography>
              </Box>
              {chapitre.niveauDifficulte && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Niveau de difficulté
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={chapitre.niveauDifficulte}
                      color={
                        chapitre.niveauDifficulte === 'facile'
                          ? 'success'
                          : chapitre.niveauDifficulte === 'moyen'
                          ? 'warning'
                          : 'error'
                      }
                      size="small"
                    />
                  </Box>
                </Box>
              )}
              {chapitre.tempsEstimeTotalMinutes && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Durée totale estimée
                  </Typography>
                  <Typography variant="body2">
                    {chapitre.tempsEstimeTotalMinutes} minutes
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Dates et Environnement
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Date de création
                </Typography>
                <Typography variant="body2">
                  {formatDate(chapitre.dateCreation, 'dayMonthLong')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Dernière modification
                </Typography>
                <Typography variant="body2">
                  {formatDate(chapitre.dateModification, 'dayMonthLong')}
                </Typography>
              </Box>
              {chapitre.datePublication && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Date de publication
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(chapitre.datePublication, 'dayMonthLong')}
                  </Typography>
                </Box>
              )}
              {chapitre.dateChargement && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Date de chargement
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(chapitre.dateChargement, 'dayMonthLong')}
                  </Typography>
                </Box>
              )}
              {chapitre.environnement && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Environnement
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={chapitre.environnement}
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Historique des statuts */}
      {chapitre.historiqueStatut && chapitre.historiqueStatut.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Historique des statuts
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {chapitre.historiqueStatut.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  borderLeft: 4,
                  borderColor: entry.statut === 'published' ? 'success.main' : 'warning.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip
                    label={entry.statut === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
                    color={entry.statut === 'draft' ? 'warning' : 'success'}
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(entry.date, 'dateTimeLong')}
                  </Typography>
                </Box>
                {entry.utilisateur && (
                  <Typography variant="body2" color="textSecondary">
                    Par: {entry.utilisateur}
                  </Typography>
                )}
                {entry.commentaire && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {entry.commentaire}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Description */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {chapitre.description}
        </Typography>
      </Paper>

      {/* Thumbnail LaTeX */}
      {chapitre.thumbnailLatex && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Thumbnail LaTeX
          </Typography>
          <Paper
            sx={{
              p: 1.5,
              mt: 1,
              bgcolor: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              overflow: 'auto',
            }}
          >
            <pre style={{ margin: 0 }}>{chapitre.thumbnailLatex}</pre>
          </Paper>
        </Paper>
      )}
    </Box>
  );
};

export default ChapitreDetail;
